import { Button } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { EstadoComponente } from "../components/ui";
import {
  EstadoFactibilidadCorporativaChoices,
  EstadoFactibilidadCorporativaENUM,
} from "../constants";
import { PlanesCorporativos } from "../interfaces";
import { IFactibilidadCorporativo } from "../interfaces/erp/factibilidad_corporativo";
import { calcColorStatusGeneral } from "../utils";

type UseTableColumnsCapacidadCorporativoProps = {
  setOpen?: any;
  setDocumentsArray?: any;
};

export const useTableColumnsCapacidadCorporativo = ({
  setOpen,
  setDocumentsArray,
}: UseTableColumnsCapacidadCorporativoProps) => {
  const columnBase = useMemo<MRT_ColumnDef<PlanesCorporativos>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "nombre",
      },
      {
        accessorKey: "capacidad",
        header: "capacidad",
      },
      {
        accessorKey: "costo_instalacion",
        header: "costo instalacion",
      },
      {
        accessorKey: "permanencia",
        header: "permanencia",
      },
      {
        accessorKey: "tipo_plan",
        header: "Tipo Plan",
      },
      {
        accessorKey: "valor",
        header: "valor",
      },
      {
        accessorKey: "estado",
        header: "estado",
        Cell: ({ row }) => (row.original.estado ? "ACTIVO" : "INACTIVO"),
      },
      {
        accessorKey: "numero_referencia",
        header: "numero referencia",
      },
      {
        accessorKey: "fecha_creacion",
        header: "fecha creacion",
        Cell: ({ row }) => dayjs(row.original.fecha_creacion).format("LLL"),
      },
    ],
    [],
  );

  const columnsSolucionCosteada = useMemo<
    MRT_ColumnDef<IFactibilidadCorporativo>[]
  >(
    () => [
      {
        accessorKey: "numero_seguimiento",
        header: "Número de Seguimiento",
        Cell: ({ row }) =>
          row.original?.preventa_corporativa_data?.numero_seguimiento || "N/A",
        enableColumnFilter: true,
      },
      {
        accessorKey: "identificacion",
        header: "Identificación",
        Cell: ({ row }) =>
          row.original?.prospecto_corporativo_data?.identificacion || "N/A",
      },
      {
        accessorKey: "razon_social",
        header: "Cliente",
        Cell: ({ row }) =>
          row.original?.prospecto_corporativo_data?.razon_social || "N/A",
        size: 300,
      },
      {
        accessorKey: "estado_factibilidad",
        header: "Estado Factibilidad",
        Cell: ({ row }) =>
          row.original.estado_factibilidad != null ? (
            <>
              <EstadoComponente
                backgroundColor={calcColorStatusGeneral(
                  row.original
                    .estado_factibilidad as EstadoFactibilidadCorporativaENUM,
                )}
                texto={row.original.estado_factibilidad || ""}
              />
              {row.original.estado_factibilidad ===
                EstadoFactibilidadCorporativaENUM.EN_REVISION &&
                row.original?.revisado_supervisor &&
                !row.original?.aprobado_supervisor && (
                  <div
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <span
                      style={{
                        color: "black",
                        fontSize: "10px",
                        textAlign: "center",
                      }}
                    >
                      Carga de documentos solicitada
                    </span>
                  </div>
                )}
              {row.original.estado_factibilidad ===
                EstadoFactibilidadCorporativaENUM.EN_REVISION &&
                row.original?.revisado_supervisor &&
                row.original?.aprobado_supervisor && (
                  <div
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <span
                      style={{
                        color: "black",
                        fontSize: "12px",
                        textAlign: "center",
                      }}
                    >
                      En aprobación final
                    </span>
                  </div>
                )}
            </>
          ) : null,
        filterVariant: "select",
        filterSelectOptions: EstadoFactibilidadCorporativaChoices,
        enableSorting: false,
      },
      {
        accessorKey: "nombre_enlace",
        header: "Nombre Enlace",
        Cell: ({ row }) => {
          const nombreBase =
            row.original.preventa_corporativa_data?.data_plan_personalizado
              ?.nombre_personalizado;
          if (!nombreBase) return "N/A";
          return row.original?.es_backup ? `${nombreBase}_BACKUP` : nombreBase;
        },
      },
      {
        accessorKey: "tipo_servicio",
        header: "Tipo de Servicio",
        Cell: ({ row }) =>
          row.original.preventa_corporativa_data?.data_plan_personalizado
            ?.tipo_plan_personalizado || "N/A",
      },
      // {
      //   accessorKey: 'condicion_venta',
      //   header: 'Condición Venta',
      //   Cell: ({ row }) => (
      //     row.original.prospecto_corporativo_data?.condicion_venta || "N/A"
      //   )
      // },
      // {
      //   accessorKey: 'es_instalado_por_tercero',
      //   header: 'Instalación Por Terceros',
      //   Cell: ({ row }) => (
      //     row.original.es_instalado_por_tercero ? 'SI' : 'NO'
      //   )
      // },
      {
        accessorKey: "canal_venta_create",
        header: "Canal de Venta",
        enableColumnFilter: false,
      },
      {
        accessorKey: "prospecto_corporativo_data.user_create",
        header: "Vendedor",
        enableColumnFilter: false,
      },
      {
        accessorKey: "area_create",
        header: "Área",
        enableColumnFilter: false,
      },
      {
        accessorKey: "url_anexo_1",
        header: "Documentos Habilitantes",
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original?.documentos?.documentos_habilitantes &&
          row.original?.documentos?.documentos_habilitantes.length > 0 ? (
            <Button
              onClick={() => {
                setOpen(true);
                setDocumentsArray({
                  documentos_habilitantes:
                    row.original?.documentos?.documentos_habilitantes,
                });
              }}
              variant="outlined"
              color="primary"
            >
              Ver Documentos
            </Button>
          ) : row.original?.url_anexo_1 ? (
            <Button
              href={row.original?.url_anexo_1}
              target="_blank"
              variant="outlined"
              color="primary"
            >
              Ver Documento
            </Button>
          ) : (
            <Button disabled variant="outlined" color="primary">
              Documento No Cargado
            </Button>
          ),
      },
      {
        accessorKey: "url_anexo_2",
        header: "Documentos Contractuales",
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original?.documentos?.documentos_contractuales &&
          row.original?.documentos?.documentos_contractuales.length > 0 ? (
            <Button
              onClick={() => {
                setOpen(true);
                setDocumentsArray({
                  documentos_contractuales:
                    row.original?.documentos?.documentos_contractuales,
                });
              }}
              variant="outlined"
              color="secondary"
            >
              Ver Documentos
            </Button>
          ) : row.original?.url_anexo_2 ? (
            <Button
              href={row.original?.url_anexo_2}
              target="_blank"
              variant="outlined"
              color="secondary"
            >
              Ver Documento
            </Button>
          ) : (
            <Button disabled variant="outlined" color="primary">
              Documento No Cargado
            </Button>
          ),
      },
      {
        accessorKey: "fecha_creacion",
        enableColumnFilter: false,
        header: "FECHA DE CREACIÓN",
        Cell: ({ row }) => dayjs(row.original.fecha_creacion).format("LLL"),
      },
      {
        accessorKey: "fecha_modificacion",
        enableColumnFilter: false,
        header: "FECHA DE MODIFICACIÓN",
        Cell: ({ row }) => dayjs(row.original.fecha_modificacion).format("LLL"),
      },
    ],
    [],
  );

  return {
    columnBase,
    columnsSolucionCosteada,
  };
};
